import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Link } from '@wartek-id/link'
import { Shimmer } from '@wartek-id/shimmer'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { Header } from 'app/KinerjaShared/components/Header'
import {
  SectionCard,
  SectionCardBody,
  SectionCardHeader,
} from 'app/KinerjaShared/components/SectionCard'
import { Banner, BannerDescription } from 'app/KinerjaShared/components/Banner'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'
import { useAPIPelaksanaanDocumentPreparation } from 'api/kinerja/useAPIKinerja'
import { PageGuardGpkPelaksanaanStatus } from 'app/KinerjaGpkPage/components/PageGuardGpkPelaksanaanStatus'
import { formatDate } from 'utils/datetime'
import { IndicatorCard } from 'app/KinerjaPage/pages/PelaksanaanV2/components/IndicatorCard'
import { useAPIPelaksanaanProgress } from 'api/kinerja/useAPIKinerja'
import { stripHTMLTags } from 'utils/text'
import { GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS } from 'app/KinerjaGpkPage/constants/contents'
import { assetUrl } from 'utils/url'

import type { MultiPeriodPayload } from 'types/kinerja'

type RincianObservasiProps = {
  periodParams?: MultiPeriodPayload
}

export function RincianObservasi({ periodParams }: RincianObservasiProps) {
  const { periodNo, periodYear } = usePeriodContext()

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (periodParams) return periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [periodParams, periodNo, periodYear])

  // -----------------------------------------
  // Get diskusi persiapan data
  // -----------------------------------------
  const {
    data: preparationDocumentData,
    isLoading: isPreparationDocumentLoading,
  } = useAPIPelaksanaanDocumentPreparation({
    tipe: 'GPK',
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const observationDate = useMemo(() => {
    return preparationDocumentData?.data?.detail?.observationDate
      ? formatDate(preparationDocumentData?.data?.detail?.observationDate)
      : '-'
  }, [preparationDocumentData?.data?.detail?.observationDate])

  // -----------------------------------------
  // Get pelaksanaan progress data
  // -----------------------------------------
  const {
    data: pelaksanaanProgressData,
    isLoading: isPelaksanaanProgressLoading,
  } = useAPIPelaksanaanProgress({
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    tipe: 'GPK',
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // -----------------------------------------
  // Resolve Praktik Kinerja asset path
  // -----------------------------------------
  const illustrationAssetPath = useMemo(() => {
    const icon =
      GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS[
        pelaksanaanProgressData?.data?.subIndicatorName
      ]

    if (icon) return assetUrl(`/illustrations/${icon}`)

    return ''
  }, [pelaksanaanProgressData?.data?.subIndicatorName])

  return (
    <main className="pt-12 px-8 pb-8">
      <div className="mx-auto max-w-7xl">
        <Header title="Rincian Observasi Praktik Kinerja" className="mb-9" />

        <PageGuardGpkPelaksanaanStatus
          periodParams={resolvedPeriodParams}
          targetStep="OBSERVATION_IMPLEMENTATION"
        >
          <SectionCard>
            <SectionCardHeader variant="light-blue">
              <Text as="h2" variant="heading-lg">
                Langkah-langkah Observasi Praktik Kinerja
              </Text>
            </SectionCardHeader>

            <SectionCardBody className="px-10 py-6">
              <div className="max-w-5xl">
                <Step number={1}>
                  <Text as="h3" variant="heading-sm" className="mb-1">
                    Observasi akan dilakukan sesuai jadwal
                  </Text>
                  <div className="mb-1">
                    <Text variant="body" color="subdued" className="mb-3">
                      Berikut jadwal observasi Anda:
                    </Text>
                    <dl className="space-y-2">
                      {isPreparationDocumentLoading ? (
                        <Shimmer noOfLines={5} className="!w-96 !h-5" />
                      ) : (
                        <>
                          <div className="flex">
                            <Text as="dt" className="w-60" color="subdued">
                              Tanggal
                            </Text>
                            <Text
                              as="dd"
                              color="subdued"
                              className="overflow-wrap-anywhere"
                            >
                              :&nbsp; {observationDate}
                            </Text>
                          </div>
                          <div className="flex">
                            <Text as="dt" className="w-60" color="subdued">
                              Lokasi Observasi
                            </Text>
                            <Text
                              as="dd"
                              color="subdued"
                              className="overflow-wrap-anywhere"
                            >
                              :&nbsp;{' '}
                              {preparationDocumentData?.data?.detail
                                ?.observationLocation?.schoolName || '-'}
                            </Text>
                          </div>
                          <div className="flex">
                            <Text as="dt" className="w-60" color="subdued">
                              Perangkat Pendukung
                            </Text>
                            <Text
                              as="dd"
                              color="subdued"
                              className="overflow-wrap-anywhere"
                            >
                              :&nbsp;{' '}
                              {preparationDocumentData?.data?.detail
                                ?.observationTool || '-'}
                            </Text>
                          </div>
                          <div className="flex">
                            <Text as="dt" className="w-60" color="subdued">
                              Catatan Lain (Opsional)
                            </Text>
                            <Text
                              as="dd"
                              color="subdued"
                              className="overflow-wrap-anywhere"
                            >
                              :&nbsp;{' '}
                              {preparationDocumentData?.data?.detail
                                ?.observationNote || '-'}
                            </Text>
                          </div>
                        </>
                      )}
                    </dl>
                  </div>

                  <Banner variant="info" className="mt-12">
                    <BannerDescription>
                      Observasi dapat dilakukan dengan beberapa cara. Silakan
                      diskusi dengan Tim Kinerja cara yang paling sesuai dengan
                      kebutuhan Anda.
                    </BannerDescription>

                    <Link
                      className="!px-0 !font-semibold"
                      href={PANDUAN_PENGELOLAAN_URL}
                      target="_blank"
                      rel="noopener"
                    >
                      Pelajari selengkapnya
                    </Link>
                  </Banner>
                </Step>

                <Step number={2}>
                  <Text as="h3" variant="heading-sm" className="mb-1">
                    Tim Kinerja akan mengisi Hasil Observasi
                  </Text>
                  <Text variant="body" color="subdued" className="mb-1">
                    Hasil observasi dapat didiskusikan untuk Pelaksanaan Tindak
                    Lanjut nantinya.
                  </Text>

                  <IndicatorCard
                    isLoading={isPelaksanaanProgressLoading}
                    className="mb-10"
                    illustrationAssetPath={illustrationAssetPath}
                    subIndicatorName={
                      pelaksanaanProgressData?.data?.subIndicatorName
                    }
                    subIndicatorDefinition={
                      pelaksanaanProgressData?.data?.subIndicatorDefinition
                    }
                    contentId={pelaksanaanProgressData?.data?.contentId}
                    qualityTarget={pelaksanaanProgressData?.data?.qualityTarget}
                  />

                  <div className="flex gap-10">
                    {isPreparationDocumentLoading ? (
                      <>
                        <Shimmer className="!h-28 !w-full" />
                        <Shimmer className="!h-28 !w-full" />
                      </>
                    ) : (
                      <>
                        <div className="w-1/2 space-y-1">
                          <Text variant="heading-sm">Target Perilaku</Text>
                          <ul className="list-disc pl-5 space-y-1">
                            {preparationDocumentData?.data?.detail?.behavioralTargets?.map(
                              (item, index) => (
                                <Text key={`behavior_${index}`} as="li">
                                  {item.behavioralTarget}
                                </Text>
                              )
                            )}
                          </ul>
                        </div>
                        <div className="w-1/2 space-y-1">
                          <Text variant="heading-sm">Upaya Mempelajari</Text>
                          <Text className="simple-wysiwyg-content overflow-wrap-anywhere">
                            {stripHTMLTags(
                              preparationDocumentData?.data?.detail
                                ?.behavioralTargetEffort
                            )}
                          </Text>
                        </div>
                      </>
                    )}
                  </div>
                </Step>

                <Step number={3} hideStepLine={false}>
                  <Text as="h3" variant="heading-sm" className="mb-1">
                    Persiapkan dengan matang dan semangat!
                  </Text>
                  <Text variant="body" color="subdued" className="mb-1">
                    Pelajari Rubrik Observasi dan informasi lainnya. Semoga
                    lancar, ya.
                  </Text>
                </Step>

                <p className="p-4">
                  <Text as="span" variant="body-bold" className="mb-1">
                    Perlu diperhatikan bahwa:
                  </Text>
                  <br />
                  <Text as="span" variant="body">
                    Rubrik Observasi hanya ditujukan untuk membantu Tim Kinerja
                    ketika observasi berlangsung.
                  </Text>
                </p>
              </div>
            </SectionCardBody>
          </SectionCard>
        </PageGuardGpkPelaksanaanStatus>
      </div>
    </main>
  )
}

function Step({
  hideStepLine = true,
  number,
  children,
}: {
  hideStepLine?: boolean
  number: number
  children: React.ReactNode
}) {
  return (
    <div className="gap-6 flex w-full">
      <div className="flex flex-col items-center gap-2">
        <div className="relative flex items-center justify-center rounded-full bg-blue-70 p-4">
          <Text as="span" color="inverse" className="absolute">
            {number}
          </Text>
        </div>

        {hideStepLine && <div className="w-px h-full bg-gray-20 mb-2" />}
      </div>

      <div className="pb-12 mb-2 w-full">{children}</div>
    </div>
  )
}
